import { Dumbbell, Salad, Droplets, MoonStar, Sparkles, Brain, Sun, Waves, Footprints, Star, Zap, Crown, HeartPulse, Gem, Scale, Ruler, BicepsFlexed, BedDouble } from "lucide-react";
import { periodKey } from "@/lib/life";

export const habits = [
  { id: "movement", label: "Intentional movement", detail: "Complete the planned session or an approved recovery alternative.", points: 25, icon: Dumbbell },
  { id: "nourishment", label: "Balanced nourishment", detail: "Build the day around satisfying, protein-forward, plant-rich meals.", points: 25, icon: Salad },
  { id: "hydration", label: "Hydration target", detail: "Follow the daily water target you set with your care team.", points: 15, icon: Droplets },
  { id: "recovery", label: "Recovery protected", detail: "Honor your wind-down and create the conditions for restorative sleep.", points: 15, icon: MoonStar },
  { id: "polish", label: "Polish ritual", detail: "Skin, hair, grooming, posture and clothes receive intentional care.", points: 10, icon: Sparkles },
  { id: "mindset", label: "Mindset reset", detail: "Choose encouragement and reflection over all-or-nothing thinking.", points: 10, icon: Brain },
];

export const glowStandards = [
  { id: "skin", title: "Skin", standard: "Consistent routine, SPF, hydration and planned professional care.", icon: Sun },
  { id: "hair", title: "Hair", standard: "A maintenance calendar that protects health and keeps styling easy.", icon: Waves },
  { id: "body", title: "Body care", standard: "Moisture, grooming, nails, feet and fragrance receive regular attention.", icon: Sparkles },
  { id: "posture", title: "Posture", standard: "Move with an open chest, grounded steps and deliberate presence.", icon: Footprints },
  { id: "style", title: "Style", standard: "Clothes fit the body you have now and reinforce who you are becoming.", icon: Star },
  { id: "confidence", title: "Confidence", standard: "Speak to yourself like someone whose future is worth protecting.", icon: Zap },
];

export const luxuryBlueprint = [
  { title: "Radiant foundation", detail: "Sleep, hydration, nutrition, movement and clinical care create the face and energy no product can fake.", cadence: "Daily", icon: HeartPulse },
  { title: "Skin excellence", detail: "A consistent routine, professional guidance when needed, and makeup that enhances your glow.", cadence: "Daily + monthly", icon: Sun },
  { title: "Crown care", detail: "Protect hair health with a wash, moisture, trim, protective-style and appointment rhythm.", cadence: "Weekly + scheduled", icon: Waves },
  { title: "Body polish", detail: "Smooth, moisturized skin; cared-for hands and feet; grooming; and a signature fragrance.", cadence: "Daily + weekly", icon: Sparkles },
  { title: "Luxury silhouette", detail: "Clothing tailored to your body now, beautiful activewear and posture that finishes every look.", cadence: "Weekly planning", icon: Gem },
  { title: "Executive presence", detail: "Strong movement, calm speech, grounded confidence and the visible ease of a prepared woman.", cadence: "Every room", icon: Crown },
];

export const weeklyPlan = [
  ["Monday", "Strength + mobility", "Build capability"],
  ["Tuesday", "Low-impact cardio or walk", "Build stamina"],
  ["Wednesday", "Recovery + beauty maintenance", "Restore and polish"],
  ["Thursday", "Strength + mobility", "Build capability"],
  ["Friday", "Low-impact cardio or walk", "Build stamina"],
  ["Saturday", "Joy movement", "Make wellness feel like life"],
  ["Sunday", "Rest + weekly setup", "Protect the next seven days"],
];

export const goalTemplates = [
  { title: "Reach my clinician-approved weight target", category: "Body Composition", unit: "lb", direction: "decrease", icon: Scale },
  { title: "Reduce my waist measurement", category: "Body Composition", unit: "in", direction: "decrease", icon: Ruler },
  { title: "Complete strength sessions each week", category: "Strength & Fitness", unit: "sessions", direction: "increase", icon: BicepsFlexed },
  { title: "Average restorative sleep nightly", category: "Recovery & Energy", unit: "hours", direction: "increase", icon: BedDouble },
  { title: "Complete my full glow routine", category: "Glow & Presence", unit: "days/month", direction: "increase", icon: Crown },
  { title: "Build consistent healthy-meal days", category: "Nutrition", unit: "days/week", direction: "increase", icon: Salad },
];

export const wellnessReviewPrompts = {
  Day: ["What did I do today to honor my body, energy or presence?", "Where did I choose grace over all-or-nothing thinking?", "What one wellness promise will I keep tomorrow?"],
  Week: ["Where did I keep promises to my body this week?", "What made healthy choices easier — or harder?", "Which three actions deserve protection next week?"],
  Month: ["What changed in my strength, energy, measurements or confidence?", "Which routine created the most visible or meaningful result?", "What will I stop, start and continue next month?"],
  Quarter: ["How does my body feel and perform differently than 90 days ago?", "What does my appearance and presence communicate now?", "Which system deserves more investment next quarter?"],
  Year: ["How did I become a woman who genuinely lives well this year?", "Which health, strength, energy and confidence indicators improved?", "What wellness standard is now permanent — and what is next?"],
};
export const reviewHorizons = ["Day", "Week", "Month", "Quarter", "Year"];
export const bodyModes = ["steady", "build", "restore"];
export const periodHorizons = ["Day", "Week", "Month", "Quarter", "Year"];

// Scheduled glow tasks grouped by cadence (checklist).
export const glowTasks = [
  { id: "skin-am", label: "Morning skin routine + SPF", cadence: "Daily", icon: Sun },
  { id: "skin-pm", label: "Evening skin routine", cadence: "Daily", icon: MoonStar },
  { id: "body-care", label: "Body moisture, grooming & fragrance", cadence: "Daily", icon: Sparkles },
  { id: "posture", label: "Posture & presence check", cadence: "Daily", icon: Footprints },
  { id: "outfit", label: "Intentional, well-fitting outfit", cadence: "Daily", icon: Star },
  { id: "hair-wash", label: "Hair wash & deep moisture", cadence: "Weekly", icon: Waves },
  { id: "nails", label: "Nails & feet care", cadence: "Weekly", icon: Sparkles },
  { id: "wardrobe-plan", label: "Plan the week's looks", cadence: "Weekly", icon: Gem },
  { id: "hair-appt", label: "Hair trim / protective style", cadence: "Monthly", icon: Crown },
  { id: "skin-pro", label: "Professional skin care", cadence: "Monthly", icon: Sun },
  { id: "wardrobe-edit", label: "Refresh & edit wardrobe", cadence: "Monthly", icon: Star },
];
export const glowCadences = ["Daily", "Weekly", "Monthly"];
export function cadenceHorizon(cadence) {
  return cadence === "Daily" ? "Day" : cadence === "Weekly" ? "Week" : "Month";
}
export function shiftDate(horizon, dateStr, dir) {
  const d = new Date(dateStr + "T12:00:00");
  if (horizon === "Day") d.setDate(d.getDate() + dir);
  else if (horizon === "Week") d.setDate(d.getDate() + dir * 7);
  else if (horizon === "Month") d.setMonth(d.getMonth() + dir);
  else if (horizon === "Quarter") d.setMonth(d.getMonth() + dir * 3);
  else d.setFullYear(d.getFullYear() + dir);
  return iso(d);
}
export function periodLabel(horizon, dateStr) {
  const d = new Date(dateStr + "T12:00:00");
  if (horizon === "Day") return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  if (horizon === "Week") { const s = new Date(d); s.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return "Week of " + s.toLocaleDateString("en-US", { month: "short", day: "numeric" }); }
  if (horizon === "Month") return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  if (horizon === "Quarter") return "Q" + (Math.floor(d.getMonth() / 3) + 1) + " " + d.getFullYear();
  return String(d.getFullYear());
}

// Canonical "day" label used across all command dashboards for a consistent format.
export function dayLabel(dateStr = iso()) {
  return periodLabel("Day", dateStr);
}

// Count consecutive completed periods (of the task's cadence) ending at the viewed date.
export function glowStreak(glowLog, taskId, cadence, dateStr) {
  const h = cadenceHorizon(cadence);
  let d = dateStr, streak = 0;
  for (let i = 0; i < 60; i++) {
    const k = periodKey(h, d);
    if ((glowLog?.[k] || {})[taskId]) { streak += 1; d = shiftDate(h, d, -1); }
    else break;
  }
  return streak;
}

export function iso(d = new Date()) {
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}
export function uid(p = "id") { return p + "-" + Date.now() + "-" + Math.random().toString(36).slice(2, 7); }

// ---------------- AI Focus streak / history ----------------
export function planFullyDone(state, dateStr) {
  const p = state?.dailyPlans?.[dateStr];
  return !!(p && p.actions && p.actions.length > 0 && p.actions.every((a) => a.done));
}
function shiftIso(dateStr, days) {
  const d = new Date(dateStr + "T12:00:00");
  d.setDate(d.getDate() - days);
  return iso(d);
}
// Consecutive days (ending today, or yesterday if today isn't finished yet) with all Focus actions done.
export function focusStreak(state, todayStr) {
  const start = planFullyDone(state, todayStr) ? 0 : 1;
  let streak = 0;
  for (let i = start; i < 400; i++) {
    if (planFullyDone(state, shiftIso(todayStr, i))) streak += 1; else break;
  }
  return streak;
}
// Last 7 days of Top-3 completion for the momentum strip.
export function focusWeek(state, todayStr) {
  const out = [];
  for (let i = 6; i >= 0; i--) {
    const ds = shiftIso(todayStr, i);
    const p = state?.dailyPlans?.[ds];
    const total = p?.actions?.length || 0;
    const done = p ? p.actions.filter((a) => a.done).length : 0;
    out.push({ date: ds, done, total });
  }
  return out;
}
export function cap(n) { return Math.max(0, Math.min(100, Math.round(Number.isFinite(n) ? n : 0))); }
export function fmtDay(dateStr) {
  try { return new Date(dateStr + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }); } catch { return dateStr; }
}

export function goalProgress(g) {
  const start = Number(g.startValue), target = Number(g.targetValue), cur = Number(g.currentValue);
  if (!Number.isFinite(target)) return 0;
  if (Number.isFinite(start) && start !== target) {
    const pct = ((cur - start) / (target - start)) * 100;
    return cap(pct);
  }
  if (g.direction === "decrease") return cap(target > 0 ? (target / Math.max(cur, 1)) * 100 : 0);
  return cap(target > 0 ? (cur / target) * 100 : 0);
}

export function computeWellnessMetrics(state, date) {
  const dayHabits = state.habits?.[date] || {};
  const dayGlow = state.glow?.[date] || {};
  const custom = state.customHabits || [];
  const planActions = (state.dailyPlans?.[date]?.actions) || [];
  const planPlanned = planActions.reduce((s, a) => s + (Number(a.points) || 0), 0);
  const planEarned = planActions.reduce((s, a) => s + (a.done ? (Number(a.points) || 0) : 0), 0);
  const planned = habits.reduce((s, h) => s + h.points, 0) + custom.reduce((s, h) => s + (h.points || 10), 0) + planPlanned;
  const earned = habits.reduce((s, h) => s + (dayHabits[h.id] ? h.points : 0), 0)
    + custom.reduce((s, h) => s + (dayHabits[h.id] ? (h.points || 10) : 0), 0) + planEarned;
  const glowDone = glowStandards.filter((g) => dayGlow[g.id]).length;
  const goals = state.goals || [];
  const goalAvg = goals.length ? cap(goals.reduce((s, g) => s + goalProgress(g), 0) / goals.length) : 0;
  const checks = (state.checkIns || []).slice().sort((a, b) => (a.loggedOn > b.loggedOn ? 1 : -1));
  const latest = checks[checks.length - 1];
  return { dayHabits, dayGlow, planned, earned, glowDone, goalAvg, checks, latest, planActions, planPlanned, planEarned };
}

// Cross-center: 0-100 wellness score to feed Life's Health & Vitality area.
export function computeWellnessScore(state) {
  const m = computeWellnessMetrics(state, iso());
  const readiness = m.planned ? (m.earned / m.planned) * 100 : 0;
  const glowPct = (m.glowDone / glowStandards.length) * 100;
  const s = state.settings || {};
  const latest = m.latest || {};
  const sleepPct = s.sleepTargetHours ? Math.min(100, ((latest.sleepHours || 0) / s.sleepTargetHours) * 100) : 0;
  const waterPct = s.waterTargetOz ? Math.min(100, ((latest.waterOz || 0) / s.waterTargetOz) * 100) : 0;
  const workoutPct = s.weeklyWorkouts ? Math.min(100, ((latest.workouts || 0) / s.weeklyWorkouts) * 100) : 0;
  const parts = [
    { v: readiness, w: 0.3 }, { v: m.goalAvg, w: 0.25 }, { v: sleepPct, w: 0.15 },
    { v: workoutPct, w: 0.15 }, { v: glowPct, w: 0.08 }, { v: waterPct, w: 0.07 },
  ];
  const tw = parts.reduce((a, p) => a + p.w, 0);
  return {
    score: cap(parts.reduce((a, p) => a + p.v * p.w, 0) / tw),
    readiness: cap(readiness), goalAvg: m.goalAvg, glowPct: cap(glowPct),
    breakdown: { readiness: cap(readiness), goals: m.goalAvg, sleep: cap(sleepPct), workouts: cap(workoutPct), glow: cap(glowPct), hydration: cap(waterPct) },
    latestWeight: latest.weight, energy: latest.energy,
  };
}
